import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { API } from 'network/api';
import { useMemo } from 'react';
import {
  CommonListParams,
  FarmerGroupLeader,
  DataPagination,
} from 'types/common';
import { cleanObject } from 'utils/helpers';

type FarmerGroupLeaderResponse = AxiosResponse<
  DataPagination<FarmerGroupLeader>
>;

type FarmerGroupLeaderParams = CommonListParams & {
  country?: string;
  agronomist?: string;
  subDistrict?: string;
  status?: string;
  'groupLeader.specified'?: boolean;
};

const useFGLList = (params: FarmerGroupLeaderParams, enabled = true) => {
  const { country, search, agronomist, subDistrict, status, ...restParams } = {
    ...params,
  };
  const queryParams = useMemo(
    () =>
      cleanObject({
        'name.contains': search,
        'country.equals': country,
        'subDistrict.in': subDistrict,
        'status.in': status,
        ...(agronomist && agronomist !== '-' && { 'agName.in': agronomist }),
        ...(agronomist && agronomist === '-' && { 'agName.specified': false }),
        ...restParams,
      }),
    [params],
  );

  const query = useQuery<FarmerGroupLeaderResponse>({
    queryKey: ['farmer-group-leader', queryParams],
    queryFn: () => API.get('/group-leader', { params: queryParams }),
    enabled: enabled,
    staleTime: 1000 * 10,
  });

  const fglOptions = useMemo(() => {
    return query.data?.data?.content?.map((data) => ({
      value: data.id,
      label: data.name,
      status: data?.status,
    }));
  }, [query.data]);

  const totalCount = query?.data?.data?.totalElements;

  return { ...query, fglOptions, totalCount };
};

export default useFGLList;
